import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue'
import { useNuxtApp } from '#app'
import { useQueryLoading } from '@vue/apollo-composable'
import Tooltip from '~/components/ix/info/Tooltip.vue'
import { useVentureId } from '~/functions/accounts'
import {
  getColor,
  getIcon,
  getImplementationStatus,
  getTooltip,
  incrementImplementationStatus,
} from '~/functions/esg'
import { getClickableLink } from '~/functions/helpers'
import { renderMarkdown } from '~/functions/markdown'
import {
  ConceptKind,
  ConceptStatus,
  ImplementationStatus,
  useCreateUserinputConceptLinkWithCustomSelectedConceptMutation,
  useGetQuestionnaireLazyQuery,
  useGetUserInputQuery,
  useGetUserQuery,
  useListConceptsQuery,
  useUnlinkSelectedConceptMutation,
  useUpdateSelectedConceptMutation,
  useUpdateUserFeedbackMutation,
  type ConceptType,
  type UserInputType,
} from '~/types/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'ActionsTable',
  props: {
    parentConcept: null,
    userInputId: null,
    questionnaireId: null,
    actionsEditable: { type: Boolean }
  },
  setup(__props: any, { expose }) {

const props = __props as {
  parentConcept: ConceptType,
  userInputId: number,
  questionnaireId: number,
  actionsEditable?: boolean
};



const { $i18n } = useNuxtApp()

const newConceptLabel = ref('')
const loading = useQueryLoading()

const { result: submeasuresResult, refetch: refetchSubmeasures } =
  useListConceptsQuery({
    queryInput: {
      submeasureOfConceptsId: props?.parentConcept?.id ?? 0,
      userInputIds: [props.userInputId],
    },
    accountId: useVentureId().value,
    withUserFeedback: true,
    limit: 100,
    offset: 0,
  })

const { result: userInputResult, refetch: refetchUserInput } = useGetUserInputQuery({
  id: props.userInputId,
})

const userInput = computed(() =>
  userInputResult.value?.getUserInput ?? { selectedConcepts: [] }
)

const submeasures = computed(() => {
  return submeasuresResult.value?.listConcepts ?? []
})

const { mutate: updateSelectedConcept } = useUpdateSelectedConceptMutation()
const { mutate: updateUserFeedback } = useUpdateUserFeedbackMutation()
const { mutate: unlinkSelectedConcept } = useUnlinkSelectedConceptMutation()

const getComment = (conceptId: number | undefined | null) => {
  const comment = userInput?.value?.selectedConcepts.find(
    (link) => link.concept?.id === conceptId
  )?.comment
  if (!conceptId || !comment) return ''
  return comment
}

const handleRateInsight = (rating: string, conceptId: number, insightId: number) => {
  updateUserFeedback({
    feedbackInput: {
      accountId: useVentureId().value,
      conceptId,
      insightId,
      feedbackType: 'qa-rating',
      feedback: rating,
    },
  }).then(() => {
    refetchSubmeasures()
  })
}

const { load: loadQuestionnaire, refetch: refetchQuestionnaire } = useGetQuestionnaireLazyQuery(
  {
    id: props.questionnaireId,
  },
  {
    fetchPolicy: 'network-only',
  }
)

const handleClickAction = (concept: any) => {
  updateSelectedConcept({
    input: {
      userInputId: userInput?.value?.id,
      conceptId: concept.id,
      implementationStatus: incrementImplementationStatus(
        getImplementationStatus(concept.id, userInput.value)
      ),
      comment: getComment(concept.id),
    },
  }).then(() => {
    loadQuestionnaire() || refetchQuestionnaire()
  })
}

const getEvidenceItem = (citation: string, url: string) => {
  if (url === '') {
    return {
      comment: $i18n.t('noConceptSourceAvailable'),
      url: '',
    }
  } else {
    return {
      comment: citation ?? '',
      url,
    }
  }
}

const getTooltipText = (
  conceptId: number | undefined | null,
  userInput: UserInputType
) => {
  return $i18n.t(getTooltip(getImplementationStatus(conceptId, userInput)))
}

const tableHeaders = computed(() => [
  {
    text: $i18n.t('general.conceptActionsTableHeaders.implementation'),
    value: 'icon',
    sortable: false,
    width: '100px',
  },
  {
    text: $i18n.t('general.conceptActionsTableHeaders.action'),
    value: 'label',
  },
  { text: '', value: 'action', sortable: false, width: '30px' },
  { text: '', value: 'data-table-expand', sortable: false, width: '30px' },
])

const tableItems = computed(() => {
  return submeasures.value.map((submeasure) => ({
    ...submeasure,
    icon: getIcon(getImplementationStatus(submeasure.id, userInput.value)),
  }))
})

const removeSelectedConcept = (conceptId: number) => {
  unlinkSelectedConcept({
    userInputId: props.userInputId ?? 0,
    conceptId,
  }).then(() => {
    refetchSubmeasures()
  })
}

const { result: userResult } = useGetUserQuery()
const email = computed(() => userResult.value?.getUser?.email)

const { mutate: createUserInputConceptLinkWithCustomSelectedConcept } =
  useCreateUserinputConceptLinkWithCustomSelectedConceptMutation()

const addMeasureAndUserInputConceptLink = async (value: string) => {
  if (!props.parentConcept.id || !props.userInputId) {
    throw new Error('Failure adding new concept. Missing ID.')
  }

  const input = {
    userInputId: props.userInputId,
    concept: {
      label: value,
      status: ConceptStatus.Proposed,
      kind: ConceptKind.Company,
      createdBy: email.value,
      isSubmeasure: true,
      additionalData: { industries: ['car_dealership'] },
      sourceUrl: '',
      citation: '',
    },
    parentConceptId: props.parentConcept.id,
    implementationStatus: ImplementationStatus.Doing,
  }

  await createUserInputConceptLinkWithCustomSelectedConcept(input)
  await refetchSubmeasures()
  await refetchUserInput()
}

const handleNewConceptInput = async () => {
  if (newConceptLabel.value.trim()) {
    await addMeasureAndUserInputConceptLink(newConceptLabel.value)
    newConceptLabel.value = ''
  }
}

const getItemClass = (item: { status: string }) => {
  return item.status === ConceptStatus.Proposed ? 'highlighted-row' : ''
}

// Expose helpers for the template
expose({
  getClickableLink,
})


return { __sfc: true,props, $i18n, newConceptLabel, loading, submeasuresResult, refetchSubmeasures, userInputResult, refetchUserInput, userInput, submeasures, updateSelectedConcept, updateUserFeedback, unlinkSelectedConcept, getComment, handleRateInsight, loadQuestionnaire, refetchQuestionnaire, handleClickAction, getEvidenceItem, getTooltipText, tableHeaders, tableItems, removeSelectedConcept, userResult, email, createUserInputConceptLinkWithCustomSelectedConcept, addMeasureAndUserInputConceptLink, handleNewConceptInput, getItemClass, Tooltip, getColor, getImplementationStatus, ConceptStatus }
}

})